import { Link, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { X, Minus, Plus, ShoppingBag } from "lucide-react";
import { useShop } from "../context/ShopContext";
import { inr } from "../lib/api";

const FREE_SHIP = 1999;

export default function CartDrawer() {
  const { cart, cartOpen, setCartOpen, updateQty, removeItem, subtotal } = useShop();
  const navigate = useNavigate();
  const remaining = Math.max(0, FREE_SHIP - subtotal);
  const pct = Math.min(100, (subtotal / FREE_SHIP) * 100);

  return (
    <AnimatePresence>
      {cartOpen && (
        <>
          <motion.div data-testid="cart-overlay" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setCartOpen(false)} className="fixed inset-0 z-[80] bg-vv-black/40 backdrop-blur-[2px]" />
          <motion.aside data-testid="mini-cart-drawer" initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
            transition={{ type: "tween", duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="fixed top-0 right-0 bottom-0 z-[90] w-full max-w-md bg-vv-ivory flex flex-col">
            <div className="flex items-center justify-between px-6 h-16 border-b border-vv-black/10">
              <p className="vv-overline">Your Bag {cart.length > 0 && `(${cart.length})`}</p>
              <button data-testid="cart-close-button" onClick={() => setCartOpen(false)} aria-label="Close bag">
                <X size={20} strokeWidth={1.25} />
              </button>
            </div>

            {cart.length === 0 ? (
              <div data-testid="cart-empty-state" className="flex-1 flex flex-col items-center justify-center gap-6 px-6 text-center">
                <ShoppingBag size={32} strokeWidth={1} className="text-vv-stone" />
                <p className="font-display text-2xl">Your bag is empty.</p>
                <button data-testid="cart-continue-shopping" onClick={() => setCartOpen(false)} className="vv-btn-dark">Continue Shopping</button>
              </div>
            ) : (
              <>
                <div className="px-6 py-4 border-b border-vv-black/10">
                  <p className="text-[10px] uppercase tracking-[0.2em] text-vv-muted mb-2" data-testid="shipping-progress-label">
                    {remaining > 0 ? `${inr(remaining)} away from free shipping` : "Your order ships free"}
                  </p>
                  <div className="h-[2px] bg-vv-black/10">
                    <div className="h-full bg-vv-forest transition-all duration-500" style={{ width: `${pct}%` }} />
                  </div>
                </div>
                <div className="flex-1 overflow-y-auto px-6 divide-y divide-vv-black/10">
                  {cart.map((item) => (
                    <div key={item.key} data-testid={`cart-item-${item.key}`} className="py-5 flex gap-4">
                      <Link to={`/product/${item.slug}`} onClick={() => setCartOpen(false)} className="w-20 h-24 bg-vv-elevated overflow-hidden shrink-0">
                        {item.image && <img src={item.image} alt={item.name} className="w-full h-full object-cover" />}
                      </Link>
                      <div className="flex-1 min-w-0">
                        <div className="flex justify-between gap-2">
                          <Link to={`/product/${item.slug}`} onClick={() => setCartOpen(false)} className="text-sm font-medium leading-snug">{item.name}</Link>
                          <button data-testid={`cart-remove-${item.key}`} onClick={() => removeItem(item.key)} aria-label="Remove item">
                            <X size={14} strokeWidth={1.5} className="text-vv-muted hover:text-vv-black" />
                          </button>
                        </div>
                        <p className="text-[11px] text-vv-muted mt-1">{item.color}{item.size ? ` · Size ${item.size}` : ""}</p>
                        <div className="flex items-center justify-between mt-3">
                          <div className="flex items-center border border-vv-black/20">
                            <button data-testid={`cart-qty-minus-${item.key}`} onClick={() => updateQty(item.key, -1)} className="px-2.5 py-1.5" aria-label="Decrease">
                              <Minus size={12} />
                            </button>
                            <span className="text-xs w-6 text-center" data-testid={`cart-qty-${item.key}`}>{item.qty}</span>
                            <button data-testid={`cart-qty-plus-${item.key}`} onClick={() => updateQty(item.key, 1)} className="px-2.5 py-1.5" aria-label="Increase">
                              <Plus size={12} />
                            </button>
                          </div>
                          <p className="text-sm font-medium">{inr(item.price * item.qty)}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="border-t border-vv-black/10 px-6 py-5 space-y-4">
                  <div className="flex justify-between text-sm">
                    <span className="text-vv-muted">Subtotal</span>
                    <span data-testid="cart-subtotal" className="font-medium">{inr(subtotal)}</span>
                  </div>
                  <p className="text-[11px] text-vv-muted">Shipping calculated at checkout. Easy 7-day returns across India.</p>
                  <button data-testid="checkout-button" onClick={() => { setCartOpen(false); navigate("/checkout"); }}
                    className="vv-btn-dark w-full">Proceed to Checkout</button>
                </div>
              </>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
